import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, Package, MapPin, Clock, CheckCircle, Truck, Ship,
  Plane, FileText, Calendar, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { mockShipments, trackingStatuses } from '../data/mock';

const TrackingPage = () => {
  const [searchParams] = useSearchParams();
  const initialId = searchParams.get('id') || '';
  const [trackingNumber, setTrackingNumber] = useState(initialId);
  const [searchedNumber, setSearchedNumber] = useState(initialId);
  const [isSearching, setIsSearching] = useState(false);

  const shipment = mockShipments.find(
    (s) => s.trackingId.toLowerCase() === searchedNumber.toLowerCase()
  );

  const handleSearch = (e) => {
    e.preventDefault();
    setIsSearching(true);
    // Simulate API call
    setTimeout(() => {
      setSearchedNumber(trackingNumber);
      setIsSearching(false);
    }, 800);
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-32 bg-[#1B4F5D]">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <span className="text-[#408D4D] font-semibold tracking-wider uppercase text-sm">Track & Trace</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6">
              Track Your Shipment
            </h1>
            <p className="text-xl text-gray-200 mb-10">
              Enter your tracking number to get real-time updates on your shipment
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="flex gap-3 bg-white/10 backdrop-blur-md p-4 rounded-xl">
                <Input
                  type="text"
                  placeholder="Enter tracking number (e.g., CBC-2025-001234)"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  className="bg-white border-0 text-[#1B4F5D] placeholder:text-gray-500 h-14 text-lg"
                />
                <Button 
                  type="submit"
                  disabled={isSearching}
                  className="bg-[#408D4D] hover:bg-[#2D6636] text-white px-8 h-14"
                >
                  {isSearching ? (
                    <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Search size={24} />
                  )}
                </Button>
              </div>
            </form>

            {/* Sample Tracking Numbers */}
            <div className="mt-6 text-sm text-gray-300">
              <span className="opacity-70">Try these demo numbers: </span>
              <button 
                onClick={() => setTrackingNumber('CBC-2025-001234')}
                className="mx-1 px-2 py-1 bg-white/10 rounded hover:bg-white/20 transition-colors"
              >
                CBC-2025-001234
              </button>
              <button 
                onClick={() => setTrackingNumber('CBC-2025-001567')}
                className="mx-1 px-2 py-1 bg-white/10 rounded hover:bg-white/20 transition-colors"
              >
                CBC-2025-001567
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16 bg-gray-50 min-h-[400px]">
        <div className="container mx-auto px-6">
          {searchedNumber && !shipment && (
            <div className="max-w-2xl mx-auto text-center py-16">
              <div className="w-24 h-24 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-6">
                <Package className="text-red-500" size={48} />
              </div>
              <h2 className="text-2xl font-bold text-[#1B4F5D] mb-4">
                Shipment Not Found
              </h2>
              <p className="text-gray-600 mb-8">
                We couldn't find a shipment with tracking number "{searchedNumber}". 
                Please check the number and try again.
              </p>
              <p className="text-sm text-gray-500">
                If you believe this is an error, please contact our support team at{' '}
                <a href="mailto:citibiz@bizinsolutions.com" className="text-[#408D4D] hover:underline">
                  citibiz@bizinsolutions.com
                </a>
              </p>
            </div>
          )}

          {shipment && (
            <div className="max-w-4xl mx-auto">
              {/* Shipment Summary */}
              <Card className="mb-8 border-0 shadow-xl overflow-hidden">
                <CardHeader className="bg-[#1B4F5D] text-white">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <p className="text-[#408D4D] text-sm font-medium mb-1">Tracking Number</p>
                      <CardTitle className="text-2xl font-bold">{shipment.trackingId}</CardTitle>
                    </div>
                    <div className="flex items-center gap-2 px-4 py-2 bg-[#408D4D] rounded-full">
                      {shipment.status.includes('Sea') ? <Ship size={20} /> : 
                       shipment.status.includes('Air') ? <Plane size={20} /> : 
                       <Truck size={20} />}
                      <span className="font-semibold">{shipment.status}</span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-4 gap-6">
                    <div className="flex items-start gap-3">
                      <MapPin className="text-[#408D4D] mt-1" size={20} />
                      <div>
                        <p className="text-sm text-gray-500">Origin</p>
                        <p className="font-semibold text-[#1B4F5D]">{shipment.origin}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="text-[#408D4D] mt-1" size={20} />
                      <div>
                        <p className="text-sm text-gray-500">Destination</p>
                        <p className="font-semibold text-[#1B4F5D]">{shipment.destination}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar className="text-[#408D4D] mt-1" size={20} />
                      <div>
                        <p className="text-sm text-gray-500">Est. Delivery</p>
                        <p className="font-semibold text-[#1B4F5D]">{shipment.estimatedDelivery}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Package className="text-[#408D4D] mt-1" size={20} />
                      <div>
                        <p className="text-sm text-gray-500">Weight</p>
                        <p className="font-semibold text-[#1B4F5D]">{shipment.weight}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Progress Tracker */}
              <Card className="mb-8 border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-[#1B4F5D]">Shipment Progress</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    {/* Progress bar */}
                    <div className="absolute top-5 left-5 right-5 h-1 bg-gray-200 rounded">
                      <div 
                        className="h-full bg-[#408D4D] rounded transition-all duration-500"
                        style={{ width: `${(shipment.currentStep / (trackingStatuses.length - 1)) * 100}%` }}
                      />
                    </div>
                    
                    {/* Steps */}
                    <div className="relative flex justify-between">
                      {trackingStatuses.slice(0, 5).map((status, index) => {
                        const isCompleted = index <= shipment.currentStep;
                        const isCurrent = index === shipment.currentStep;
                        return (
                          <div key={index} className="flex flex-col items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all duration-300 ${
                              isCompleted 
                                ? 'bg-[#408D4D] text-white' 
                                : 'bg-gray-200 text-gray-400'
                            } ${isCurrent ? 'ring-4 ring-[#408D4D]/30' : ''}`}>
                              {isCompleted ? <CheckCircle size={20} /> : (index + 1)}
                            </div>
                            <p className={`mt-3 text-xs text-center max-w-[80px] ${
                              isCompleted ? 'text-[#1B4F5D] font-medium' : 'text-gray-400'
                            }`}>
                              {status}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Shipment History */}
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-[#1B4F5D]">Tracking History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-0">
                    {shipment.history.map((event, index) => (
                      <div key={index} className="relative pl-8 pb-8 last:pb-0">
                        {/* Timeline line */}
                        {index < shipment.history.length - 1 && (
                          <div className="absolute left-3 top-6 w-0.5 h-full bg-[#408D4D]/20" />
                        )}
                        {/* Timeline dot */}
                        <div className={`absolute left-0 top-1.5 w-6 h-6 rounded-full flex items-center justify-center ${
                          index === 0 ? 'bg-[#408D4D]' : 'bg-[#408D4D]/20'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${
                            index === 0 ? 'bg-white' : 'bg-[#408D4D]'
                          }`} />
                        </div>
                        {/* Content */}
                        <div>
                          <p className="text-sm text-gray-500 mb-1">{event.date}</p>
                          <p className="font-semibold text-[#1B4F5D]">{event.status}</p>
                          <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                            <MapPin size={14} />
                            {event.location}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {!searchedNumber && (
            <div className="max-w-2xl mx-auto text-center py-16">
              <div className="w-24 h-24 mx-auto rounded-full bg-[#408D4D]/10 flex items-center justify-center mb-6">
                <Search className="text-[#408D4D]" size={48} />
              </div>
              <h2 className="text-2xl font-bold text-[#1B4F5D] mb-4">
                Enter Your Tracking Number
              </h2>
              <p className="text-gray-600">
                Input your CitiBiz Connections tracking number above to see real-time 
                updates on your shipment status.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrackingPage;
